import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import useQuestionnaireStore from "../store/useQuestionnaireStore";
import { updateReportStatus } from "../services/dbService";
import {
  ChevronLeft,
  Printer,
  User,
  Activity,
  AlertCircle,
  FileText,
  Calendar,
  ShieldAlert,
  Mail,
  CheckCircle2,
  Clock
} from "lucide-react";
import { toast } from "react-toastify";

/**
 * Summary Component
 * Reused for the Admin Panel to display detailed patient data.
 * It detects if data is passed via navigation state (Admin mode) 
 * or falls back to the local store (Patient mode/Debug).
 */
const Summary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const localStore = useQuestionnaireStore();
  
  // Use data passed from Admin Dashboard, OR fallback to local store for direct viewing/debugging
  const initialData = location.state?.patientData || localStore;
  const isReadOnly = !!location.state?.patientData;

  const [data, setData] = useState(initialData);
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper to format values for clinical display
  const formatValue = (val) => {
    if (val === true) return "Yes";
    if (val === false) return "No";
    if (val === null || val === undefined || val === "") return "Not Recorded";
    if (Array.isArray(val)) return val.length > 0 ? val.join(", ") : "None Selected";
    return val;
  };

  /**
   * Toggles the clinical review status of the report.
   * Only available in Admin (read-only) mode.
   */
  const handleStatusToggle = async () => {
    if (!isReadOnly || !data.id) return;
    
    setIsUpdating(true);
    const newStatus = data.status === 'reviewed' ? 'new' : 'reviewed';
    
    try {
        await updateReportStatus(data.id, newStatus);
        setData(prev => ({ ...prev, status: newStatus }));
        toast.success(`Report marked as ${newStatus}`);
    } catch (error) {
        toast.error("Failed to update clinical status");
        console.error("Status update error:", error);
    } finally {
        setIsUpdating(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      className="page summary-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="summary-header">
        <div className="header-top">
          <button onClick={() => navigate(-1)} className="back-button">
            <ChevronLeft size={20} /> {isReadOnly ? "Back to Dashboard" : "Back"}
          </button>
          
          <div className="action-group">
            {isReadOnly && (
                <button 
                    className={`status-toggle-btn ${data.status === 'reviewed' ? 'reviewed' : 'pending'}`}
                    onClick={handleStatusToggle}
                    disabled={isUpdating}
                >
                    {data.status === 'reviewed' ? (
                        <><CheckCircle2 size={18} /> Reviewed</>
                    ) : (
                        <><Clock size={18} /> Mark as Reviewed</>
                    )}
                </button>
            )}
            <button className="print-button" onClick={() => window.print()}>
                <Printer size={20} /> Print Record
            </button>
          </div>
        </div>
        
        <h1>{isReadOnly ? "Clinical Patient Record" : "Assessment Summary"}</h1>
        <p>
            {isReadOnly 
                ? `Reviewing detailed triage data for: ${data.name || 'Anonymous'}` 
                : "Please review the information collected during the assessment."}
        </p>
      </div>

      <div className="report-grid">
        
        {/* Contact Info Section */}
        {data.email && (
            <motion.section className="report-card full-width" variants={itemVariants}>
            <div className="card-header">
                <Mail className="card-icon" size={22} />
                <h3>Patient Contact Info</h3>
            </div>
            <div className="card-content">
                <div className="data-row">
                    <span className="label">Registered Name:</span>
                    <span className="value">{data.name}</span>
                </div>
                <div className="data-row">
                    <span className="label">Registered Number:</span>
                    <span className="value">{data.phone}</span>
                </div>
                <div className="data-row">
                    <span className="label">Registered Email:</span>
                    <span className="value">{data.email}</span>
                </div>
            </div>
            </motion.section>
        )}

        {/* 1. Patient Profile */}
        <motion.section className="report-card" variants={itemVariants}>
          <div className="card-header">
            <User className="card-icon" size={22} />
            <h3>Demographics</h3>
          </div>
          <div className="card-content">
            <div className="data-row">
              <span className="label">Age Range:</span>
              <span className="value">{formatValue(data.age)}</span>
            </div>
            <div className="data-row">
              <span className="label">Gender:</span>
              <span className="value">{formatValue(data.gender)}</span>
            </div>
          </div>
        </motion.section>

        {/* 2. Pain Analysis Card (Highlight Section) */}
        <motion.section className="report-card pain-analysis-card" variants={itemVariants}>
          <div className="card-header">
            <Activity className="card-icon" size={22} />
            <h3>Pain Characteristics</h3>
          </div>
          <div className="pain-grid">
            <div className="pain-metric">
              <span className="metric-label">Joint Location</span>
              <span className="metric-value highlight">
                {formatValue(data.painLocation)}
              </span>
            </div>
            <div className="pain-metric">
              <span className="metric-label">Precise Structure</span>
              <span className="metric-value">
                {data.preciseLocation?.name || "General"}
                <span className="sub-text">
                  (ID: {data.preciseLocation?.id || "-"})
                </span>
              </span>
            </div>
            <div className="pain-metric">
              <span className="metric-label">Pain Severity (VAS)</span>
              <div
                className="intensity-badge"
                style={{
                  "--intensity-color":
                    data.painIntensity > 7 ? "#ef5350" : 
                    data.painIntensity > 4 ? "#ffca28" : "#66bb6a",
                }}
              >
                {data.painIntensity !== null ? `${data.painIntensity}/10` : "N/A"}
              </div>
            </div>
            <div className="pain-metric">
              <span className="metric-label">Sensation Type</span>
              <span className="metric-value">
                {formatValue(data.painType)}
              </span>
            </div>
          </div>
        </motion.section>

        {/* 3. Onset & Timeline */}
        <motion.section className="report-card" variants={itemVariants}>
          <div className="card-header">
            <Calendar className="card-icon" size={22} />
            <h3>Timeline & Onset</h3>
          </div>
          <div className="card-content">
            <div className="data-row">
              <span className="label">History Length:</span>
              <span className="value">{formatValue(data.painDuration)}</span>
            </div>
            <div className="data-row">
              <span className="label">Onset Mode:</span>
              <span className="value" style={{ textTransform: "capitalize" }}>
                {formatValue(data.onset)}
              </span>
            </div>
            {data.onset === "sudden" && (
              <div className="data-row">
                <span className="label">Primary Trigger:</span>
                <span className="value">{formatValue(data.onsetDetail)}</span>
              </div>
            )}
          </div>
        </motion.section>

        {/* 4. Clinical Factors */}
        <motion.section className="report-card" variants={itemVariants}>
          <div className="card-header">
            <AlertCircle className="card-icon" size={22} />
            <h3>Behavioral Factors</h3>
          </div>
          <div className="card-content">
            <div className="factor-block aggravating">
              <span className="label">Aggravating Factors:</span>
              <p>{formatValue(data.aggravatingFactor)}</p>
            </div>
            <div className="factor-block relieving">
              <span className="label">Relieving Factors:</span>
              <p>{formatValue(data.relievingFactor)}</p>
            </div>
          </div>
        </motion.section>

        {/* 5. Physical Signs */}
        <motion.section className="report-card" variants={itemVariants}>
          <div className="card-header">
            <ShieldAlert className="card-icon" size={22} />
            <h3>Physical Findings</h3>
          </div>
          <div className="card-content">
            <div className="data-row">
              <span className="label">Visible Swelling:</span>
              <span className={`value ${data.hasSwelling ? "alert-text" : ""}`}>
                {formatValue(data.hasSwelling)}
              </span>
            </div>
            <div className="data-row">
              <span className="label">Pressure Sensitivity:</span>
              <span className={`value ${data.worsensWithPressure ? "alert-text" : ""}`}>
                {formatValue(data.worsensWithPressure)}
              </span>
            </div>
          </div>
        </motion.section>

        {/* 6. Medical History */}
        <motion.section className="report-card full-width" variants={itemVariants}>
          <div className="card-header">
            <FileText className="card-icon" size={22} />
            <h3>Reported Medical History</h3>
          </div>
          <div className="chips-container">
            {data.medicalConditions && data.medicalConditions.length > 0 ? (
              data.medicalConditions.map((cond, i) => (
                <span key={i} className="history-chip">
                  {cond}
                </span>
              ))
            ) : (
              <span className="empty-text">
                No comorbidities or relevant history reported by patient.
              </span>
            )}
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
};

export default Summary;